import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { useUserStore } from './userStore'
import type { NavigationItem } from '@/types/navigation'
import type { RouteRecordRaw, RouteLocationNamedRaw } from 'vue-router'

const lgBreakpoint = 976

export const useSidebarStore = defineStore('sidebar', () => {
  const isMobile = ref(window.innerWidth < lgBreakpoint)
  const isSidebarExpanded = ref(window.innerWidth >= lgBreakpoint)
  const navigationItems = ref<NavigationItem[]>([])
  const navigationHistory = ref<string[]>([])

  const sidebarIsExpanded = computed(() => isSidebarExpanded.value)
  const screenIsMobile = computed(() => isMobile.value)
  const userNavigationItems = computed(() => navigationItems.value)

  const hasExpandedSubmenu = computed(() => {
    const checkExpanded = (items: NavigationItem[]): boolean => {
      return items.some((item) => {
        if (item.meta.is_expanded) return true
        if (item.children) return checkExpanded(item.children)
        return false
      })
    }
    return checkExpanded(navigationItems.value)
  })

  const visibleNavigationItems = computed(() => {
    if (navigationHistory.value.length === 0) {
      return navigationItems.value
    }

    let currentItems = navigationItems.value
    for (const path of navigationHistory.value) {
      const item = currentItems.find((i) => i.path === path)
      if (item?.children) {
        currentItems = item.children
      }
    }
    return currentItems
  })

  const currentExpandedItem = computed(() => {
    if (navigationHistory.value.length === 0) return null

    let currentItem: NavigationItem | null = null
    let currentItems = navigationItems.value

    for (const path of navigationHistory.value) {
      const found = currentItems.find((i) => i.path === path)
      if (found) {
        currentItem = found
        if (found.children) {
          currentItems = found.children
        }
      }
    }
    return currentItem
  })

  function toggleExpanded() {
    isSidebarExpanded.value = !isSidebarExpanded.value
  }

  function updateIsMobile() {
    isMobile.value = window.innerWidth < lgBreakpoint
  }

  function findItemRecursively(
    items: NavigationItem[],
    path: string
  ): NavigationItem | null {
    for (const item of items) {
      if (item.path === path) return item
      if (item.children) {
        const found = findItemRecursively(item.children, path)
        if (found) return found
      }
    }
    return null
  }

  function expandSubmenu(item: NavigationItem): RouteLocationNamedRaw | null {
    const foundItem = findItemRecursively(navigationItems.value, item.path)
    if (foundItem && foundItem.children?.length) {
      foundItem.meta.is_expanded = true
      navigationHistory.value.push(item.path)

      if (!isMobile.value) {
        const firstChild = findFirstAccessibleChild(foundItem.children)
        if (firstChild?.name) {
          return { name: firstChild.name }
        }
      }
    }
    return null
  }

  function collapseSubmenu(): RouteLocationNamedRaw | null {
    if (navigationHistory.value.length === 0) return null

    const lastPath = navigationHistory.value.pop()!
    const item = findItemRecursively(navigationItems.value, lastPath)

    if (item) {
      item.meta.is_expanded = false
    }

    if (navigationHistory.value.length > 0) {
      const parentPath =
        navigationHistory.value[navigationHistory.value.length - 1]
      const parentItem = findItemRecursively(navigationItems.value, parentPath)

      if (parentItem?.children?.length && !isMobile.value) {
        const firstChild = findFirstAccessibleChild(parentItem.children)
        if (firstChild?.name) {
          return { name: firstChild.name }
        }
      }
    }

    return { name: 'home' }
  }

  function collapseAllSubmenus() {
    const collapseRecursively = (items: NavigationItem[]) => {
      items.forEach((item) => {
        if (item.meta.is_expanded) {
          item.meta.is_expanded = false
        }
        if (item.children) {
          collapseRecursively(item.children)
        }
      })
    }
    collapseRecursively(navigationItems.value)
    navigationHistory.value = []
  }

  function initialiseNavigationItems() {
    const router = useRouter()
    const userStore = useUserStore()
    navigationItems.value = filterNavigationItems(
      router.options.routes as RouteRecordRaw[],
      userStore
    )

    router.isReady().then(() => {
      const currentRouteName = router.currentRoute.value.name as string
      expandToCurrentRoute(currentRouteName)
    })
  }

  function expandToCurrentRoute(currentRouteName: string) {
    const expandPath = (items: NavigationItem[]): boolean => {
      for (const item of items) {
        if (item.children) {
          const hasMatchingDescendant = findRouteByName(
            item.children,
            currentRouteName
          )

          if (hasMatchingDescendant) {
            item.meta.is_expanded = true
            navigationHistory.value.push(item.path)
            expandPath(item.children)
            return true
          }
        }
      }
      return false
    }

    expandPath(navigationItems.value)
  }

  function findRouteByName(
    items: NavigationItem[],
    name: string
  ): boolean {
    for (const item of items) {
      if (item.name === name) return true
      if (item.children && findRouteByName(item.children, name)) {
        return true
      }
    }
    return false
  }

  function toNavigationItem(route: RouteRecordRaw): NavigationItem {
    return {
      path: route.path,
      name: route.name as string | undefined,
      meta: {
        ...route.meta,
        is_expanded: route.meta?.is_expanded ?? false,
      },
    }
  }

  function hasPermission(
    meta: RouteRecordRaw['meta'],
    userStore: ReturnType<typeof useUserStore>
  ): boolean {
    if (meta?.permissions_needed) {
      return userStore.userHasOneOrMoreOfPermissions(meta.permissions_needed)
    }
    return true
  }

  function filterNavigationItems(
    routes: RouteRecordRaw[],
    userStore: ReturnType<typeof useUserStore>
  ): NavigationItem[] {
    const filterItems = (items: RouteRecordRaw[]): NavigationItem[] => {
      return items
        .filter((item) => item?.meta?.show_on_sidenav && hasPermission(item.meta, userStore))
        .map((item) => {
          const navItem = toNavigationItem(item)
          if (item.children) {
            navItem.children = filterItems(item.children)
          }
          return navItem
        })
    }

    return routes.flatMap((item) => {
      if (item?.meta?.show_on_sidenav && hasPermission(item.meta, userStore)) {
        const navItem = toNavigationItem(item)
        if (item.children) {
          navItem.children = filterItems(item.children)
        }
        return navItem
      }
      return []
    })
  }

  function routeIsActive(itemName: string): boolean {
    const router = useRouter()
    const currentRoute = router.currentRoute.value
    return currentRoute.name === itemName
  }

  function findFirstAccessibleChild(
    children: NavigationItem[]
  ): NavigationItem | null {
    if (!children || children.length === 0) return null

    const firstChild = children[0]

    if (!firstChild.children || firstChild.children.length === 0) {
      return firstChild
    }

    return findFirstAccessibleChild(firstChild.children)
  }

  return {
    isMobile,
    isSidebarExpanded,
    navigationItems,
    navigationHistory,
    sidebarIsExpanded,
    screenIsMobile,
    userNavigationItems,
    hasExpandedSubmenu,
    visibleNavigationItems,
    currentExpandedItem,
    toggleExpanded,
    updateIsMobile,
    expandSubmenu,
    collapseSubmenu,
    collapseAllSubmenus,
    initialiseNavigationItems,
    routeIsActive,
  }
})

window.addEventListener('resize', () => {
  const sidebarStore = useSidebarStore()
  sidebarStore.updateIsMobile()
})
